'use strict';

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.catchError = catchError;
exports.addGlobalMiddleware = addGlobalMiddleware;
exports.removeAllGlobalMiddlewares = removeAllGlobalMiddlewares;
exports.asyncStacktrace = asyncStacktrace;
exports.catchAsyncStacktrace = catchAsyncStacktrace;
exports.syncStacktrace = syncStacktrace;
exports.catchSyncStacktrace = catchSyncStacktrace;

var _autoTraceHelper = require('./auto-trace.helper.js');

var globalMiddlewares = [];

/**
 * Returns a method that will handle error processing and throwing. Pass this as your onError arg in RxJS subscriptions.
 * This is meant to replace all the other catch and stacktrace methods. https://xkcd.com/927/
 *
 * RxJS Subscription example:
 *   myObs.subscribe(onComplete, catchError())
 *
 * If a callback is passed then that will be called with the processed error instead of throwing the error.
 * e.g.
 *   myObs.subscribe(onComplete, catchError((error, throwError) => {
 *     error.showToast = false
 *     throwError(error)
 *   }))
 */
function catchError(callback, extraContext) {
  if (callback instanceof Error) {
    console.warn('catchError must be called before being passed to an onError handler.\nFind any instance of `catchError` and replace with `catchError()`.\nAlso check that an error object is not being passed into catchError as the first parameter should be a function.\n We call catchError to wrap the error with additional information, so the following error might be missing some additional useful context.');
    throw callback;
  }
  return asyncStacktrace(function (error) {
    if (callback) {
      callback(error, function throwError(err) {
        throw err;
      });
    } else {
      throw error;
    }
  }, extraContext);
}

/**
 * Adds a middleware function that will be called on all errors before being handled by auto-trace
 *
 * @param {function (Object err) => Object newErr } middlewareFn
 */
function addGlobalMiddleware(middlewareFn) {
  globalMiddlewares.push(middlewareFn);
}

/**
 * Removes all middlewares
 */
function removeAllGlobalMiddlewares() {
  globalMiddlewares = [];
}

/**
 * Wraps rawError in an Error object (if typeOf rawError != Error) using the async stacktrace
 * Calls globalMiddleware functions on rawError before wrapping in Error object
 * In most cases the async stacktrace is most useful - it contains the stacktrace before switching context to caller timeout
 *
 * @param {function (Object err) => Object newErr } [callback] - function that will be called with the error
 * @param {Object} rawError
 * @returns {Error}
 */
function asyncStacktrace() {
  var callback = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : function () {};
  var extraContext = arguments[1];

  var asyncStacktraceErr = (0, _autoTraceHelper.createError)();
  var syncMiddlewareErrFunctions = executeAsyncMiddleware(asyncStacktraceErr);

  return function (rawError) {
    var middlewareErr = executeSyncMiddleware(syncMiddlewareErrFunctions, rawError);
    var errOut = (0, _autoTraceHelper.wrapObjectWithError)(middlewareErr, asyncStacktraceErr, extraContext);
    return callback(errOut);
  };
}

/**
 * Wraps rawError in an Error object (if typeOf rawError != Error) using the async stacktrace
 * Calls globalMiddleware functions on rawError before wrapping in Error object
 * Behaves exactly as asyncStacktrace, but throws the error object instead of returning.
 * In most cases the async stacktrace is most useful - it contains the stacktrace before switching context to caller timeout
 *
 * @param {function (Object err) => Object newErr } [callback] - function that will be called with the error
 * @param {Object} rawError
 * @returns {Error}
 */
function catchAsyncStacktrace(extraContext) {
  var asyncStacktraceErr = (0, _autoTraceHelper.createError)();
  var syncMiddlewareErrFunctions = executeAsyncMiddleware(asyncStacktraceErr);

  return function (rawError) {
    var middlewareErr = executeSyncMiddleware(syncMiddlewareErrFunctions, rawError);
    var errOut = (0, _autoTraceHelper.wrapObjectWithError)(middlewareErr, asyncStacktraceErr, extraContext);
    setTimeout(function () {
      throw errOut;
    });
  };
}

/**
 * Wraps rawError in an Error object (if typeOf rawError != Error) using the default stacktrace
 * Calls globalMiddleware functions on rawError before wrapping in Error object
 * sync stacktrace contains the stacktrace after switching context to caller timeout
 *
 * @param {Object} rawError
 * @returns {Error}
 */
function syncStacktrace(rawError) {
  var syncStacktraceErr = (0, _autoTraceHelper.createError)();
  var syncMiddlewareErrFunctions = executeAsyncMiddleware(syncStacktraceErr);
  var middlewareErr = executeSyncMiddleware(syncMiddlewareErrFunctions, rawError);
  var syncErr = (0, _autoTraceHelper.wrapObjectWithError)(middlewareErr);
  return syncErr;
}

/**
 * Wraps rawError in an Error object (if typeOf rawError != Error) using the default stacktrace
 * Calls globalMiddleware functions on rawError before wrapping in Error object
 * Behaves exactly as syncStacktrace, but throws the error object instead of returning.
 * sync stacktrace contains the stacktrace after switching context to caller timeout
 *
 * @param {Object} rawError
 * @throws {Error}
 */
function catchSyncStacktrace(rawError) {
  var syncStacktraceErr = (0, _autoTraceHelper.createError)();
  var syncMiddlewareErrFunctions = executeAsyncMiddleware(syncStacktraceErr);
  var middlewareErr = executeSyncMiddleware(syncMiddlewareErrFunctions, rawError);
  var syncErr = (0, _autoTraceHelper.wrapObjectWithError)(middlewareErr);
  setTimeout(function () {
    throw syncErr;
  });
}

/**
 * Calls async middlewares on asyncErr
 *
 * @param   {Object} asyncErr
 * @returns  Array of Functions
 */
function executeAsyncMiddleware(asyncErr) {
  return globalMiddlewares.map(function (middleware) {
    return middleware(asyncErr);
  });
}

/**
 * Calls Sync middlewares on ogErr
 * @param  {array of functions} middlewares - the middlewares to call on ogErr
 * @param  {Error} ogErr - original gansta error
 * @return {Error} the new error, after it has been passed through the middlewares
 */
function executeSyncMiddleware(middlewares, ogErr) {
  //Don't run middlewares on errors that have already been handled by auto-trace
  if (ogErr && ogErr.autoTraceIgnore) {
    return ogErr;
  }
  return middlewares.reduce(function (syncErr, middleware) {
    if (typeof middleware === 'function') {
      return middleware(syncErr);
    } else {
      throw new Error("the middleware passed to auto-trace did not return a function, see docs for auto-trace middleware https://github.com/CanopyTax/auto-trace/blob/master/README.md");
    }
  }, ogErr);
}